import { FileText } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { EmptyState, ErrorState, LoadingBlock } from "@/components/ui/States";
import { coachApi } from "@/lib/api";
import { formatLongDate } from "@/lib/date";

/** Nilai `payroll_basis` mengikuti core Flexv. */
const PAYROLL_BASIS_LABELS: Record<string, string> = {
    flat_per_session: "Flat per session",
    hourly: "Hourly",
    per_pax: "Per participant",
    revenue_share: "Revenue share",
    monthly: "Monthly",
};

const TAX_LABELS: Record<string, string> = {
    withheld_by_merchant: "Withheld by merchant",
    self_reported: "Self reported",
    not_applicable: "Not applicable",
};

function formatCurrency(value: number | null): string {
    if (value === null) return "—";
    return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0,
    }).format(value);
}

export function ContractPage() {
    const { data, isPending, isError, error, refetch } = useQuery({
        queryKey: ["coach-contracts"],
        queryFn: ({ signal }) => coachApi.contracts(signal),
    });

    return (
        <div className="flex flex-col gap-6">
            <header>
                <h1 className="app-text-primary text-2xl font-bold sm:text-3xl">Contract</h1>
                <p className="app-shell-text-muted mt-1 text-sm">
                    How your coaching sessions are paid.
                </p>
            </header>

            {isPending && <LoadingBlock label="Loading contract..." />}

            {isError && (
                <ErrorState
                    message={error instanceof Error ? error.message : "Failed to load contract."}
                    onRetry={() => void refetch()}
                />
            )}

            {data && data.length === 0 && (
                <Card>
                    <EmptyState
                        icon={<FileText className="h-6 w-6" />}
                        title="No active contract"
                        description="Your payroll basis has not been configured yet."
                    />
                </Card>
            )}

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {data?.map((contract) => (
                    <Card key={contract.id}>
                        <CardHeader
                            title={contract.outlet?.name ?? "All outlets"}
                            description={`Effective from ${formatLongDate(contract.effectiveFrom)}`}
                            icon={<FileText className="h-5 w-5" />}
                            action={
                                contract.effectiveUntil ? (
                                    <Badge tone="neutral">Ended</Badge>
                                ) : (
                                    <Badge tone="success">Active</Badge>
                                )
                            }
                        />
                        <CardContent className="flex flex-col gap-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="app-text-soft text-xs uppercase tracking-wide">
                                        Payroll basis
                                    </p>
                                    <p className="app-text-primary text-sm font-semibold">
                                        {contract.payrollBasis
                                            ? (PAYROLL_BASIS_LABELS[contract.payrollBasis] ??
                                              contract.payrollBasis)
                                            : "—"}
                                    </p>
                                </div>
                                <div>
                                    <p className="app-text-soft text-xs uppercase tracking-wide">
                                        Rate
                                    </p>
                                    <p className="app-text-primary text-sm font-semibold">
                                        {formatCurrency(contract.rateAmount)}
                                    </p>
                                </div>
                                <div>
                                    <p className="app-text-soft text-xs uppercase tracking-wide">
                                        Tax handling
                                    </p>
                                    <p className="app-text-primary text-sm font-semibold">
                                        {contract.taxHandling
                                            ? (TAX_LABELS[contract.taxHandling] ?? contract.taxHandling)
                                            : "—"}
                                    </p>
                                </div>
                                <div>
                                    <p className="app-text-soft text-xs uppercase tracking-wide">
                                        Ends
                                    </p>
                                    <p className="app-text-primary text-sm font-semibold">
                                        {contract.effectiveUntil
                                            ? formatLongDate(contract.effectiveUntil)
                                            : "Ongoing"}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}
